import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import ConfirmSheet from '@/components/ConfirmSheet';
import { Package, Eye, Trash2, CheckCircle2, XCircle, Sprout, PackageX, PackageCheck } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Admin moderation sheet for a farmer's produce posts.
 * Admins can delete any post and toggle its availability (mark sold / relist).
 */
export default function AdminFarmerPostsSheet({ farmer, open, onOpenChange, onCountChange }) {
  const { api } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [toggleTarget, setToggleTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!farmer?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/farmer/${farmer.id}/produce`);
      setPosts(data.posts || []);
    } catch {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, [api, farmer]);

  useEffect(() => {
    if (open && farmer?.id) load();
  }, [open, farmer, load]);

  const confirmToggle = async () => {
    const post = toggleTarget;
    if (!post) return;
    setTogglingId(post.id);
    try {
      await api.put(`/produce/${post.id}`, { is_available: !post.is_available });
      setPosts(prev => prev.map(p => p.id === post.id ? { ...p, is_available: !post.is_available } : p));
      toast.success(!post.is_available ? 'Marked as available' : 'Marked as sold');
      setToggleTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setTogglingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/produce/${deleteTarget.id}`);
      setPosts(prev => prev.filter(p => p.id !== deleteTarget.id));
      toast.success('Post deleted');
      setDeleteTarget(null);
      onCountChange?.(-1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          data-testid="admin-farmer-posts-sheet"
          className="p-0 rounded-t-3xl overflow-hidden flex flex-col max-h-[92dvh]"
        >
          <div className="flex justify-center pt-3 pb-1" aria-hidden>
            <div className="w-10 h-1 rounded-full bg-[#E4E8EF]" />
          </div>
          <div className="px-6 pt-3 pb-4">
            <SheetHeader className="text-left space-y-1">
              <SheetTitle className="text-xl font-bold text-[#0B1620] font-['Bricolage_Grotesque'] tracking-tight">
                {farmer?.name || 'Unnamed farmer'}'s posts
              </SheetTitle>
              <SheetDescription className="text-[13px] text-[#5B6675]">
                {posts.length} post{posts.length === 1 ? '' : 's'} · manage or delete on this farmer's behalf.
              </SheetDescription>
            </SheetHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 pb-8 space-y-2.5">
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-24 rounded-2xl skeleton" />
              ))
            ) : posts.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-[#F6F8FB] border border-[#E4E8EF] flex items-center justify-center mb-3">
                  <Sprout className="w-6 h-6 text-[#5B6675]" />
                </div>
                <p className="text-sm font-semibold text-[#0B1620]">No posts yet</p>
                <p className="text-xs text-[#5B6675] mt-1">This farmer hasn't posted anything.</p>
              </div>
            ) : (
              posts.map((p) => (
                <div
                  key={p.id}
                  data-testid={`admin-post-row-${p.id}`}
                  className={`bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF] p-3 flex items-start gap-3 ${!p.is_available ? 'opacity-75' : ''}`}
                >
                  {p.images?.[0] ? (
                    <img src={p.images[0]} alt="" className={`w-16 h-16 rounded-xl object-cover flex-shrink-0 ${!p.is_available ? 'grayscale' : ''}`} />
                  ) : (
                    <div className="w-16 h-16 rounded-xl bg-[#EFF2F7] flex items-center justify-center flex-shrink-0">
                      <Package className="w-5 h-5 text-[#5B6675]" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2">
                      <p className="text-sm font-semibold text-[#0B1620] truncate flex-1">{p.title}</p>
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${
                          p.is_available
                            ? 'bg-[#15803D]/10 text-[#15803D]'
                            : 'bg-[#B91C1C]/10 text-[#B91C1C]'
                        }`}
                      >
                        {p.is_available ? 'Available' : 'Sold'}
                      </span>
                    </div>
                    <p className="text-[11px] text-[#5B6675] mt-0.5 truncate">
                      ₹{p.price_per_unit}/{p.unit} · {p.quantity} {p.unit}
                    </p>
                    <div className="mt-1 flex items-center gap-3 text-[10.5px] text-[#5B6675] tabular-nums">
                      <span className="flex items-center gap-0.5"><Eye className="w-3 h-3" /> {p.views || 0}</span>
                    </div>
                    <div className="mt-2 flex gap-1.5">
                      <button
                        data-testid={`admin-toggle-post-${p.id}`}
                        onClick={() => setToggleTarget(p)}
                        disabled={togglingId === p.id}
                        className={`flex-1 h-8 rounded-full text-[11px] font-semibold flex items-center justify-center gap-1 transition-colors border ${
                          p.is_available
                            ? 'bg-[#F6F8FB] text-[#B91C1C] border-[#B91C1C]/20 hover:bg-[#B91C1C]/8'
                            : 'bg-[#F6F8FB] text-[#15803D] border-[#15803D]/20 hover:bg-[#15803D]/8'
                        } disabled:opacity-50`}
                      >
                        {p.is_available
                          ? (<><XCircle className="w-3.5 h-3.5" /> Mark sold</>)
                          : (<><CheckCircle2 className="w-3.5 h-3.5" /> Mark available</>)}
                      </button>
                      <button
                        data-testid={`admin-delete-post-${p.id}`}
                        onClick={() => setDeleteTarget(p)}
                        className="w-8 h-8 rounded-full text-[#5B6675] hover:text-[#DC2626] hover:bg-[#DC2626]/8 flex items-center justify-center transition-colors"
                        aria-label="Delete post"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>

      <ConfirmSheet
        open={!!toggleTarget}
        onOpenChange={(o) => !o && setToggleTarget(null)}
        title={toggleTarget?.is_available ? 'Mark this post as sold?' : 'Mark this post as available?'}
        description={
          toggleTarget
            ? toggleTarget.is_available
              ? `"${toggleTarget.title}" will be hidden from vendor search results and marked sold-out. You can re-list it later.`
              : `"${toggleTarget.title}" will be visible again in vendor search results and shown as available.`
            : ''
        }
        confirmLabel={toggleTarget?.is_available ? 'Mark sold' : 'Mark available'}
        cancelLabel="Cancel"
        destructive={!!toggleTarget?.is_available}
        icon={toggleTarget?.is_available
          ? <PackageX className="w-6 h-6" strokeWidth={2.2} />
          : <PackageCheck className="w-6 h-6" strokeWidth={2.2} />
        }
        onConfirm={confirmToggle}
        loading={!!togglingId}
        testId="admin-toggle-post"
      />

      <ConfirmSheet
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete this post?"
        description={deleteTarget ? `"${deleteTarget.title}" will be removed from the marketplace permanently. This can't be undone.` : ''}
        confirmLabel="Delete"
        cancelLabel="Keep it"
        destructive
        icon={<Trash2 className="w-6 h-6" strokeWidth={2.2} />}
        onConfirm={confirmDelete}
        loading={deleting}
        testId="admin-delete-post"
      />
    </>
  );
}
